import { queryKeys } from '@/lib/query-keys';
import { apiClient } from '@borradh-workspace/api-client';
import type {
  OrganizationServiceStockClips,
  ServiceFootageSpec,
  SetServiceFootageSpecBody,
  SetServiceStockClipsBody,
  SetServiceStockClipsResult,
} from '@borradh-workspace/api-client/types';
import {
  organizationServiceStockClipsResponseSchema,
  setServiceFootageSpecResponseSchema,
  setServiceStockClipsResponseSchema,
} from '@borradh-workspace/contracts';
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';

export const organizationServiceStockClipsQueryOptions = (
  organizationId: string
) =>
  queryOptions({
    queryKey: queryKeys.adminTerminal.serviceStockFootage(organizationId),
    queryFn: () =>
      apiClient.get<OrganizationServiceStockClips>(
        `admin-terminal/organizations/${encodeURIComponent(organizationId)}/stock-footage`,
        { schema: organizationServiceStockClipsResponseSchema }
      ),
    enabled: !!organizationId,
    // Previews are signed links that expire; a page left open for a long time
    // refetches rather than showing dead players.
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

/** One clinic's services and the stock clips each one uses (admin). */
export const useOrganizationServiceStockClips = (organizationId: string) => {
  const query = useQuery(
    organizationServiceStockClipsQueryOptions(organizationId)
  );
  return {
    stockFootage: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
};

/**
 * Save the clips an admin chose for one service. They replace the automatic
 * match; an empty list goes back to it.
 */
export const useSetServiceStockClips = (organizationId: string) => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: {
      serviceId: string;
      body: SetServiceStockClipsBody;
    }) =>
      apiClient.put<SetServiceStockClipsResult>(
        `admin-terminal/organizations/${encodeURIComponent(organizationId)}/stock-footage/services/${encodeURIComponent(input.serviceId)}`,
        input.body,
        { schema: setServiceStockClipsResponseSchema }
      ),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.adminTerminal.serviceStockFootage(organizationId),
      });
      const n = result.stockClipIds.length;
      toast.success(
        result.mode === 'chosen'
          ? `Saved. This service's videos now use ${n === 1 ? 'this clip' : `these ${n} clips`}.`
          : 'Saved. This service is back on the automatic match.'
      );
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Could not save the clips');
    },
  });

  return {
    setServiceClips: (
      serviceId: string,
      stockClipIds: string[],
      onDone?: () => void,
      excludedStockClipIds?: string[]
    ) =>
      mutation.mutate(
        {
          serviceId,
          body: {
            stockClipIds,
            ...(excludedStockClipIds ? { excludedStockClipIds } : {}),
          },
        },
        onDone ? { onSuccess: () => onDone() } : undefined
      ),
    isSaving: mutation.isPending,
  };
};

const serviceTagsPath = (
  organizationId: string,
  serviceId: string,
  clinic = false
) =>
  clinic
    ? `organization-services/${encodeURIComponent(serviceId)}/footage`
    : `admin-terminal/organizations/${encodeURIComponent(organizationId)}/stock-footage/services/${encodeURIComponent(serviceId)}/tags`;

/**
 * Fix what a service shows on camera: its treatment and body areas. The AI
 * never re-reads the service after this, and its automatic clips follow.
 */
export const useSetServiceTags = (organizationId: string, clinic = false) => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: {
      serviceId: string;
      body: SetServiceFootageSpecBody;
    }) =>
      apiClient.put<ServiceFootageSpec>(
        serviceTagsPath(organizationId, input.serviceId, clinic),
        input.body,
        { schema: setServiceFootageSpecResponseSchema }
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.adminTerminal.serviceStockFootage(organizationId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.serviceFootage.all(),
      });
      toast.success('Saved. Its automatic clips now follow the fix.');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Could not save the tags');
    },
  });

  return {
    setServiceTags: (
      serviceId: string,
      body: SetServiceFootageSpecBody,
      onDone?: () => void
    ) =>
      mutation.mutate(
        { serviceId, body },
        onDone ? { onSuccess: () => onDone() } : undefined
      ),
    isSavingTags: mutation.isPending,
  };
};
